/**
 * useAi Hook
 * Manages the AI generation pipeline for OpenSCAD code
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  AiMode,
  AiStep,
  AiGenerationState,
  AiStepResult,
  AiTokenUsage,
  AiPromptTemplates,
  INITIAL_GENERATION_STATE,
  MAX_BUGFIX_ATTEMPTS,
  OpenRouterModel,
} from '@/types/ai';
import {
  createAiClient,
  streamChatCompletion,
  ChatMessage,
  ChatContentPart,
  calculateCost,
  aggregateTokenUsage,
} from '@/services/ai';
import {
  buildPlanUserMessage,
  buildImplementUserMessage,
  buildFixUserMessage,
  extractCodeFromResponse,
} from '@/services/aiPrompts';

export interface UseAiOptions {
  promptTemplates: AiPromptTemplates;
  onCodeGenerated?: (code: string) => void;
  onError?: (error: string) => void;
  onStepComplete?: (step: AiStep, result: AiStepResult) => void;
  compileCode?: (code: string) => Promise<{ success: boolean; error?: string }>;
}

export interface UseAiReturn {
  // State
  state: AiGenerationState;
  isGenerating: boolean;
  canGenerate: boolean;

  // Token tracking
  tokenUsage: AiTokenUsage;

  // Actions
  generate: (
    apiKey: string,
    mode: AiMode,
    prompt: string,
    model: string,
    modelInfo: OpenRouterModel | null,
    existingCode?: string,
    previewImageDataUrl?: string,
  ) => Promise<void>;
  approvePlan: () => void;
  requestPlanRevision: (feedback: string) => void;
  cancel: () => void;
  reset: () => void;
}

export function useAi(options: UseAiOptions): UseAiReturn {
  const {
    promptTemplates,
    onCodeGenerated,
    onError,
    onStepComplete,
    compileCode,
  } = options;

  const [state, setState] = useState<AiGenerationState>(
    INITIAL_GENERATION_STATE,
  );
  const [modelInfo, setModelInfo] = useState<OpenRouterModel | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const isGeneratingRef = useRef(false);

  // Store workflow state for plan approval
  const workflowStateRef = useRef<{
    apiKey?: string;
    mode?: AiMode;
    prompt?: string;
    model?: string;
    existingCode?: string;
    analysis?: string;
    previewImageDataUrl?: string;
    client?: ReturnType<typeof createAiClient>;
  }>({});

  // Derived state
  // Note: planApproval is NOT considered "generating" since it waits for user input
  const isGenerating =
    state.step !== 'idle' &&
    state.step !== 'complete' &&
    state.step !== 'error' &&
    state.step !== 'planApproval';
  const canGenerate = !isGenerating && state.step !== 'planApproval';

  // Calculate token usage from step results
  const tokenUsage = useMemo((): AiTokenUsage => {
    const usages = state.results.map((r) => r.tokens);
    const aggregated = aggregateTokenUsage(usages);

    let estimatedCost = 0;
    if (modelInfo) {
      estimatedCost = calculateCost(
        modelInfo,
        aggregated.promptTokens,
        aggregated.completionTokens,
      );
    }

    return {
      ...aggregated,
      estimatedCost,
    };
  }, [state.results, modelInfo]);

  // Update state helper
  const updateState = useCallback((updates: Partial<AiGenerationState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  // Add step result helper
  const addStepResult = useCallback(
    (result: AiStepResult) => {
      setState((prev) => ({
        ...prev,
        results: [...prev.results, result],
      }));
      onStepComplete?.(result.step, result);
    },
    [onStepComplete],
  );

  // Run a single step with streaming
  const runStep = useCallback(
    async (
      client: ReturnType<typeof createAiClient>,
      model: string,
      step: AiStep,
      systemPrompt: string,
      userMessage: string | ChatContentPart[],
    ): Promise<{
      content: string;
      usage: { prompt: number; completion: number };
    }> => {
      const startTime = Date.now();

      updateState({ step, currentContent: '' });

      const messages: ChatMessage[] = [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage },
      ];

      const result = await streamChatCompletion(
        client,
        model,
        messages,
        {
          onChunk: (chunk) => {
            setState((prev) => ({
              ...prev,
              currentContent: prev.currentContent + chunk,
            }));
          },
        },
        abortControllerRef.current?.signal,
      );

      const stepResult: AiStepResult = {
        step,
        content: result.content,
        tokens: result.usage,
        durationMs: Date.now() - startTime,
      };

      addStepResult(stepResult);

      return result;
    },
    [updateState, addStepResult],
  );

  // Continue from plan approval to implementation
  const continueFromPlanApproval = useCallback(async () => {
    if (!workflowStateRef.current.client || !workflowStateRef.current.model) {
      return;
    }

    const { client, model, mode, existingCode } = workflowStateRef.current;
    const plan = state.plan;

    if (!plan) {
      updateState({ step: 'error', error: 'No plan available to implement' });
      return;
    }

    try {
      let generatedCode: string | undefined;

      // Step 3: Implement
      const implementUserMessage = buildImplementUserMessage(
        plan,
        mode === 'modify' ? existingCode : undefined,
      );
      const implementResult = await runStep(
        client,
        model,
        'implement',
        promptTemplates.implement,
        implementUserMessage,
      );
      generatedCode = extractCodeFromResponse(implementResult.content);

      // Step 4: Test (compile)
      updateState({ step: 'test', generatedCode });

      if (compileCode) {
        let compileResult = await compileCode(generatedCode);
        let bugfixAttempts = 0;

        // Step 5: Bugfix loop (if needed)
        while (!compileResult.success && bugfixAttempts < MAX_BUGFIX_ATTEMPTS) {
          bugfixAttempts++;
          updateState({
            bugfixAttempts,
            compilationError: compileResult.error,
          });

          const fixUserMessage = buildFixUserMessage(
            generatedCode,
            compileResult.error || 'Unknown compilation error',
            bugfixAttempts,
          );

          const fixResult = await runStep(
            client,
            model,
            'bugfix',
            promptTemplates.fix,
            fixUserMessage,
          );

          generatedCode = extractCodeFromResponse(fixResult.content);
          updateState({ generatedCode });

          // Re-test
          updateState({ step: 'test' });
          compileResult = await compileCode(generatedCode);
        }

        if (!compileResult.success) {
          // Max attempts reached, but we still have code
          updateState({
            step: 'complete',
            generatedCode,
            compilationError: compileResult.error,
          });
          onCodeGenerated?.(generatedCode);
        } else {
          // Success!
          updateState({ step: 'complete', generatedCode });
          onCodeGenerated?.(generatedCode);
        }
      } else {
        // No compile function provided, just complete
        updateState({ step: 'complete', generatedCode });
        onCodeGenerated?.(generatedCode);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred';

      if (errorMessage !== 'Generation cancelled') {
        updateState({ step: 'error', error: errorMessage });
        onError?.(errorMessage);
      }
    } finally {
      isGeneratingRef.current = false;
      abortControllerRef.current = null;
    }
  }, [
    promptTemplates,
    runStep,
    updateState,
    compileCode,
    onCodeGenerated,
    onError,
    state.plan,
  ]);

  // Main generation function - runs up to plan approval
  const generate = useCallback(
    async (
      apiKey: string,
      mode: AiMode,
      prompt: string,
      model: string,
      modelInfoParam: OpenRouterModel | null,
      existingCode?: string,
      previewImageDataUrl?: string,
    ) => {
      if (isGeneratingRef.current) return;
      isGeneratingRef.current = true;

      // Reset state
      setState({
        ...INITIAL_GENERATION_STATE,
        mode,
      });
      setModelInfo(modelInfoParam);

      // Create abort controller
      abortControllerRef.current = new AbortController();

      const client = createAiClient(apiKey);

      // Store workflow state for plan approval continuation
      workflowStateRef.current = {
        apiKey,
        mode,
        prompt,
        model,
        existingCode,
        previewImageDataUrl,
        client,
      };

      try {
        let analysis: string | undefined;

        // Step 1: Analyze (modify mode only)
        if (mode === 'modify' && existingCode) {
          // Build user message - include preview image if available (multimodal)
          let analyzeUserMessage: string | ChatContentPart[];
          if (previewImageDataUrl) {
            analyzeUserMessage = [
              { type: 'text', text: existingCode },
              {
                type: 'text',
                text: '\n\nThe following image shows the current 3D preview of this code:',
              },
              {
                type: 'image_url',
                imageUrl: { url: previewImageDataUrl },
              },
            ];
          } else {
            analyzeUserMessage = existingCode;
          }

          const analyzeResult = await runStep(
            client,
            model,
            'analyze',
            promptTemplates.analyze,
            analyzeUserMessage,
          );
          analysis = analyzeResult.content;
          workflowStateRef.current.analysis = analysis;
        }

        // Step 2: Plan
        // Include preview image in plan step too if available and in create mode
        let planUserMessage: string | ChatContentPart[];
        const planText = buildPlanUserMessage(prompt, analysis);
        if (previewImageDataUrl && mode === 'create') {
          planUserMessage = [
            { type: 'text', text: planText },
            {
              type: 'text',
              text: '\n\nThe following image shows the current 3D preview for reference:',
            },
            {
              type: 'image_url',
              imageUrl: { url: previewImageDataUrl },
            },
          ];
        } else {
          planUserMessage = planText;
        }
        const planResult = await runStep(
          client,
          model,
          'plan',
          promptTemplates.plan,
          planUserMessage,
        );
        const plan = planResult.content;

        // Step 3: Wait for plan approval
        updateState({
          step: 'planApproval',
          plan,
          currentContent: plan,
        });
        isGeneratingRef.current = false;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';

        if (errorMessage !== 'Generation cancelled') {
          updateState({ step: 'error', error: errorMessage });
          onError?.(errorMessage);
        }
        isGeneratingRef.current = false;
        abortControllerRef.current = null;
      }
    },
    [promptTemplates, runStep, updateState, onError],
  );

  // Approve the plan and continue to implementation
  const approvePlan = useCallback(() => {
    if (state.step !== 'planApproval' || !state.plan) return;

    isGeneratingRef.current = true;
    continueFromPlanApproval();
  }, [state.step, state.plan, continueFromPlanApproval]);

  // Request a revision to the plan
  const requestPlanRevision = useCallback(
    async (feedback: string) => {
      if (state.step !== 'planApproval' || !state.plan) return;

      const { client, model, prompt, analysis } = workflowStateRef.current;

      if (!client || !model) return;

      isGeneratingRef.current = true;

      try {
        // Re-plan with the user's feedback
        const revisedPlanUserMessage = buildPlanUserMessage(
          `${prompt}\n\nUser feedback on previous plan:\n${feedback}\n\nPlease revise the plan based on this feedback.`,
          analysis,
        );

        updateState({ step: 'plan', currentContent: '' });

        const planResult = await runStep(
          client,
          model,
          'plan',
          promptTemplates.plan,
          revisedPlanUserMessage,
        );
        const revisedPlan = planResult.content;

        // Wait for approval again
        updateState({
          step: 'planApproval',
          plan: revisedPlan,
          currentContent: revisedPlan,
        });
        isGeneratingRef.current = false;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error occurred';

        if (errorMessage !== 'Generation cancelled') {
          updateState({ step: 'error', error: errorMessage });
          onError?.(errorMessage);
        }
        isGeneratingRef.current = false;
      }
    },
    [state.step, state.plan, promptTemplates, runStep, updateState, onError],
  );

  // Cancel generation
  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    updateState({ step: 'idle' });
    isGeneratingRef.current = false;
    workflowStateRef.current = {};
  }, [updateState]);

  // Reset state
  const reset = useCallback(() => {
    setState(INITIAL_GENERATION_STATE);
    setModelInfo(null);
    isGeneratingRef.current = false;
    workflowStateRef.current = {};
  }, []);

  return {
    state,
    isGenerating,
    canGenerate,
    tokenUsage,
    generate,
    approvePlan,
    requestPlanRevision,
    cancel,
    reset,
  };
}
